import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import './Services.css';

const Services = () => {
  const mainServices = [
    {
      icon: 'bi-briefcase-fill',
      title: 'Consulting Services',
      description: 'Expert guidance tailored to your unique challenges and goals.',
      features: [
        'Strategic Planning & Decision Making',
        'Business Development Support',
        'Personal Development Coaching',
        'Financial Planning Assistance',
        'Career Transition Guidance',
      ],
      color: 'bg-primary-teal',
    },
    {
      icon: 'bi-link-45deg',
      title: 'Resource Connections',
      description: 'Connecting you with vital community resources and support systems.',
      features: [
        'Healthcare & Medical Services',
        'Housing Assistance Programs',
        'Educational Opportunities',
        'Food & Nutrition Support',
        'Legal Aid & Advocacy',
      ],
      color: 'bg-primary-orange',
    },
    {
      icon: 'bi-person-workspace',
      title: 'Job Placement Services',
      description: 'Matching your skills with meaningful employment opportunities.',
      features: [
        'Resume Writing & Review',
        'Interview Preparation',
        'Job Search Strategies',
        'Employer Connections',
        'Career Counseling',
      ],
      color: 'bg-primary-purple',
    },
    {
      icon: 'bi-calculator-fill',
      title: 'Tax Services',
      description: 'Professional tax preparation and planning to maximize your returns.',
      features: [
        'Individual Tax Preparation',
        'Small Business Tax Filing',
        'Tax Credit Identification',
        'Year-Round Tax Planning',
        'IRS Representation',
      ],
      color: 'bg-primary-teal',
    },
  ];

  const additionalServices = [
    {
      icon: 'bi-translate',
      title: 'Translation Services',
      description: 'Breaking language barriers to ensure everyone has equal access.',
    },
    {
      icon: 'bi-file-earmark-text-fill',
      title: 'Document Assistance',
      description: 'Help with filling out forms, applications, and important documents.',
    },
    {
      icon: 'bi-mortarboard-fill',
      title: 'Educational Workshops',
      description: 'Free workshops on financial literacy, job skills, and more.',
    },
    {
      icon: 'bi-people-fill',
      title: 'Family Support',
      description: 'Resources and guidance for family-related challenges and needs.',
    },
    {
      icon: 'bi-house-heart-fill',
      title: 'Housing Assistance',
      description: 'Support finding affordable housing and rental assistance programs.',
    },
    {
      icon: 'bi-cash-coin',
      title: 'Financial Literacy',
      description: 'Education on budgeting, saving, credit building, and financial planning.',
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Initial Consultation',
      description: 'We start with a free consultation to understand your needs and goals.',
    },
    {
      step: '02',
      title: 'Customized Plan',
      description: 'We create a personalized action plan tailored to your situation.',
    },
    {
      step: '03',
      title: 'Resource Connection',
      description: 'We connect you with the right resources and opportunities.',
    },
    {
      step: '04',
      title: 'Ongoing Support',
      description: 'We provide continuous guidance and support throughout your journey.',
    },
  ];

  return (
    <div className="services-page mt-4">
      {/* Hero Section */}
      <section className="c-services-hero position-relative overflow-hidden py-5 py-lg-6" style={{background: 'linear-gradient(135deg, rgba(236, 232, 247, 0.6) 0%, rgba(255, 255, 255, 1) 100%)'}}>
        <div className="position-absolute top-0 start-0 w-100 h-100" style={{backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.08) 0%, transparent 50%)'}}></div>
        <div className="container position-relative">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <AnimatedSection className="text-center">
                <div className="c-hero-badge bg-primary-teal bg-opacity-10 text-primary-teal d-inline-flex align-items-center px-4 py-2 rounded-pill mb-4 shadow-sm">
                  <i className="bi bi-briefcase-fill me-2 fs-5"></i>
                  <span className="fw-bold">Professional Services</span>
                </div>
                <h1 className="display-3 fw-bold mb-4 text-dark" style={{letterSpacing: '-1px'}}>
                  Our Services
                </h1>
                <p className="fs-4 text-muted lh-lg mb-5 mx-auto" style={{maxWidth: '700px'}}>
                  Comprehensive support services designed to help you achieve your goals
                  and build a brighter future.
                </p>
                <div className="c-hero-features d-flex flex-wrap justify-content-center gap-4 mb-4">
                  <div className="c-feature-badge bg-white rounded-pill px-4 py-3 shadow-sm d-inline-flex align-items-center">
                    <i className="bi bi-check-circle-fill text-success me-2 fs-5"></i>
                    <span className="fw-semibold text-dark">Free Consultation</span>
                  </div>
                  <div className="c-feature-badge bg-white rounded-pill px-4 py-3 shadow-sm d-inline-flex align-items-center">
                    <i className="bi bi-shield-check text-primary-teal me-2 fs-5"></i>
                    <span className="fw-semibold text-dark">Expert Guidance</span>
                  </div>
                  <div className="c-feature-badge bg-white rounded-pill px-4 py-3 shadow-sm d-inline-flex align-items-center">
                    <i className="bi bi-heart-fill text-primary-orange me-2 fs-5"></i>
                    <span className="fw-semibold text-dark">Personalized Care</span>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="main-services section-padding">
        <div className="container">
          <AnimatedSection className="section-title">
            <h2>Core Services</h2>
            <p>Expert solutions for your most important needs</p>
          </AnimatedSection>

          <div className="row g-4">
            {mainServices.map((service, index) => (
              <AnimatedSection className="col-lg-6" key={index} delay={index * 0.1}>
                <motion.div
                  className="service-card-detailed"
                  whileHover={{ y: -10 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className="service-header">
                    <div className={`service-icon-large ${service.color}`}>
                      <i className={service.icon}></i>
                    </div>
                    <div>
                      <h3>{service.title}</h3>
                      <p className="service-subtitle">{service.description}</p>
                    </div>
                  </div>
                  <div className="service-features">
                    <h5>What's Included:</h5>
                    <ul>
                      {service.features.map((feature, idx) => (
                        <li key={idx}>
                          <i className="bi bi-check-circle-fill text-success"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link to="/contact" className="btn btn-sm btn-primary-teal">
                    Get Started
                    <i className="bi bi-arrow-right ms-2"></i>
                  </Link>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section section-padding bg-gradient-section">
        <div className="container">
          <AnimatedSection className="section-title text-center">
            <h2 className="text-white">How We Work</h2>
            <p className="text-white-50">A simple, effective process to get you the help you need</p>
          </AnimatedSection>

          <div className="row g-4">
            {process.map((item, index) => (
              <AnimatedSection className="col-lg-3 col-md-6" key={index} delay={index * 0.1}>
                <motion.div
                  className="process-card"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className="process-number">{item.step}</div>
                  <h5>{item.title}</h5>
                  <p>{item.description}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="additional-services section-padding">
        <div className="container">
          <AnimatedSection className="section-title">
            <h2>Additional Services</h2>
            <p>More ways we can support your journey</p>
          </AnimatedSection>

          <div className="row g-4">
            {additionalServices.map((service, index) => (
              <AnimatedSection className="col-lg-4 col-md-6" key={index} delay={index * 0.1}>
                <motion.div
                  className="additional-service-card"
                  whileHover={{ y: -5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <i className={`${service.icon} service-icon-small text-primary-purple`}></i>
                  <h5>{service.title}</h5>
                  <p>{service.description}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="why-choose section-padding bg-primary-light">
        <div className="container">
          <div className="row align-items-center">
            <AnimatedSection className="col-lg-6">
              <h2 className="mb-4">Why Choose Our Services?</h2>
              <div className="why-list">
                <div className="why-item">
                  <i className="bi bi-check-circle-fill text-primary-teal"></i>
                  <div>
                    <h5>Personalized Approach</h5>
                    <p>Every client receives customized support tailored to their unique situation.</p>
                  </div>
                </div>
                <div className="why-item">
                  <i className="bi bi-check-circle-fill text-primary-orange"></i>
                  <div>
                    <h5>Experienced Professionals</h5>
                    <p>Our team has decades of combined experience serving diverse communities.</p>
                  </div>
                </div>
                <div className="why-item">
                  <i className="bi bi-check-circle-fill text-primary-purple"></i>
                  <div>
                    <h5>Comprehensive Resources</h5>
                    <p>Access to an extensive network of community partners and resources.</p>
                  </div>
                </div>
                <div className="why-item">
                  <i className="bi bi-check-circle-fill text-primary-teal"></i>
                  <div>
                    <h5>Proven Track Record</h5>
                    <p>Thousands of success stories from satisfied clients over 15+ years.</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection className="col-lg-6" delay={0.2}>
              <div className="stats-grid">
                <div className="stat-box">
                  <h3 className="text-primary-teal">100%</h3>
                  <p>Free Consultation</p>
                </div>
                <div className="stat-box">
                  <h3 className="text-primary-orange">24/7</h3>
                  <p>Support Available</p>
                </div>
                <div className="stat-box">
                  <h3 className="text-primary-purple">50+</h3>
                  <p>Services Offered</p>
                </div>
                <div className="stat-box">
                  <h3 className="text-primary-teal">15+</h3>
                  <p>Years Experience</p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section-padding">
        <div className="container">
          <AnimatedSection className="text-center">
            <h2 className="mb-4">Ready to Get Started?</h2>
            <p className="lead mb-4">
              Let's discuss how we can help you achieve your goals. Contact us today for a free consultation.
            </p>
            <Link to="/contact" className="btn btn-primary-teal btn-lg">
              Schedule Your Free Consultation
              <i className="bi bi-calendar-check ms-2"></i>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Services;
